import { CdkAccordion } from '@angular/cdk/accordion';
import {
  AfterContentInit,
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  contentChildren,
  ContentChildren,
  input,
  Input,
  QueryList,
  ViewEncapsulation,
} from '@angular/core';
import { HxAccordionPanel } from './accordion-panel';

@Component({
  selector: 'hx-accordion',
  standalone: true,
  templateUrl: './accordion.html',
  styleUrls: ['./accordion.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class]': "`hx-accordion` + ' ' + (customCssClass() || '')",
  },
  encapsulation: ViewEncapsulation.None,
})
export class HxAccordion extends CdkAccordion implements AfterContentInit {
  /**
   * Whether multiple accordion panels can be expanded simultaneously. When false, only one panel can be expanded at a
   * time.
   *
   * @default true
   */
  @Input({ transform: booleanAttribute })
  override multi = true;

  /**
   * Whether to display section numbers (1., 2., 3., etc.) before panel labels.
   *
   * @default false
   */
  readonly showSectionNumbers = input<boolean>(false);

  /**
   * Icon name to display when accordion panels are expanded. Must be a valid icon name from the Harmony icon library
   * (hx-icon component). Falls back to 'chevron-up' if not provided or empty string.
   *
   * Applied to all child panels in this accordion.
   *
   * @example
   *   ```html
   *   <hx-accordion [expandedIcon]="'remove'">
   *     <hx-accordion-panel>Content</hx-accordion-panel>
   *   </hx-accordion>
   *   ```;
   *
   * @default 'chevron-up'
   */
  readonly expandedIcon = input<string>('chevron-up');

  /**
   * Icon name to display when accordion panels are collapsed. Must be a valid icon name from the Harmony icon library
   * (hx-icon component). Falls back to 'chevron-down' if not provided or empty string.
   *
   * Applied to all child panels in this accordion.
   *
   * @example
   *   ```html
   *   <hx-accordion [collapsedIcon]="'add'">
   *     <hx-accordion-panel>Content</hx-accordion-panel>
   *   </hx-accordion>
   *   ```;
   *
   * @default 'chevron-down'
   */
  readonly collapsedIcon = input<string>('chevron-down');

  /**
   * Custom CSS classes to append to the accordion host element.
   *
   * @example
   *   ```html
   *   <hx-accordion customCssClass="my-custom-class">
   *     <hx-accordion-panel>Content</hx-accordion-panel>
   *   </hx-accordion>
   *   ```;
   */
  readonly customCssClass = input<string>('');

  @ContentChildren(HxAccordionPanel, { descendants: true })
  readonly panels!: QueryList<HxAccordionPanel>;

  /**
   * Signal-based query for tracking panels (for section numbering). This is kept in sync with the QueryList for
   * reactive updates.
   */
  readonly panelsSignal = contentChildren(HxAccordionPanel, { descendants: true });

  ngAfterContentInit(): void {
    // Subscribe to panel expansion changes to enforce single-expansion mode
    // when multi=false, closing all other panels when one opens
    this.panels.changes.subscribe(() => this._setupPanelListeners());
    this._setupPanelListeners();
  }

  /**
   * Sets up listeners for all panels to handle single-expansion mode. When a panel opens and multi=false, all other
   * panels should close.
   */
  private _setupPanelListeners(): void {
    this.panels.forEach((panel) => {
      // Listen to the opened event from each panel
      panel.opened.subscribe(() => {
        if (!this.multi) {
          this._closeAllExcept(panel);
        }
      });
    });
  }

  /** Closes all panels except the specified one. Used to enforce single-expansion mode when multi=false. */
  private _closeAllExcept(exceptPanel: HxAccordionPanel): void {
    this.panels.forEach((panel) => {
      if (panel !== exceptPanel && panel.expanded && !panel.disabled) {
        panel.close();
      }
    });
  }

  /**
   * Expands all enabled panels.
   *
   * - If multi=true: Expands all enabled panels
   * - If multi=false: Expands only the first enabled panel
   */
  expandAll(): void {
    if (this.multi) {
      // Multi-expansion mode: expand all enabled panels
      this.panels.forEach((panel) => {
        if (!panel.disabled) {
          panel.open();
        }
      });
    } else {
      // Single-expansion mode: expand only first enabled panel
      const firstEnabled = this.panels.find((panel) => !panel.disabled);
      if (firstEnabled) {
        firstEnabled.open();
      }
    }
  }

  /** Collapses all enabled panels. */
  collapseAll(): void {
    this.panels.forEach((panel) => {
      if (!panel.disabled) {
        panel.close();
      }
    });
  }
}
